import React from 'react';

// Shared utility functions for text processing across all report card components

/**
 * Converts a string to sentence case (first letter uppercase, rest lowercase)
 * Handles multiple words and special characters properly
 * @param str - The string to convert
 * @returns The string in sentence case
 */
export const toSentenceCase = (str: string | undefined | null): string => {
  if (!str || typeof str !== 'string') return '';
  
  // Split by word boundaries while preserving special characters
  return str
    .toLowerCase()
    .replace(/(?:^|\s|-)\S/g, (match) => match.toUpperCase());
};

/**
 * Substitutes variables in {} brackets with values from data objects
 * @param text - The input text to process
 * @param data - Primary data object for variable substitution
 * @param baseData - Secondary data object for variable substitution
 * @param player - Player object for player-specific variables
 * @param playerAssessment - Player assessment object for assessment-specific variables
 * @returns String with variables substituted
 */
export const substituteVariables = (
  text: string, 
  data?: any, 
  baseData?: any, 
  player?: IPlayer, 
  playerAssessment?: IPlayerAssessment
): string => {
  if (!text || typeof text !== 'string') return text;

  // Create a combined data object for variable lookup
  const combinedData = {
    ...baseData,
    ...data,
    // Add common player variables with sentence case
    playerFirstName: toSentenceCase(player?.firstName),
    playerLastName: toSentenceCase(player?.lastName),
    playerName: player ? toSentenceCase(`${player.firstName} ${player.lastName}`.trim()) : undefined,
    // Add common assessment variables
    assessmentName: playerAssessment?.assessment?.name,
    companyName: playerAssessment?.assessment?.company?.name,
    positionName: playerAssessment?.position?.name,
    ageGroupName: playerAssessment?.ageGroup?.name,
  };

  // Replace variables in {} brackets
  return text.replace(/\{([^}]+)\}/g, (match, variableName) => {
    const trimmedName = variableName.trim();
    
    // Handle nested property access (e.g., {player.firstName})
    const value = getNestedProperty(combinedData, trimmedName);
    
    // If variable found, return the value
    if (value !== undefined && value !== null) {
      return String(value);
    }
    
    // Log error for missing variable and return original bracket notation
    console.error(`Variable substitution failed: Variable '{${trimmedName}}' not found in data`, {
      availableVariables: Object.keys(combinedData),
      data,
      baseData
    });
    
    return match;
  });
};

/**
 * Helper function to get nested properties from an object using dot notation
 * @param obj - The object to search in
 * @param path - The property path (e.g., 'player.firstName')
 * @returns The value at the path or undefined
 */
const getNestedProperty = (obj: any, path: string): any => {
  if (!obj || !path) return undefined;
  
  const value = path.split('.').reduce((current, key) => {
    return current?.[key];
  }, obj);
  
  // Apply sentence case to player name fields when accessed directly
  if (path.startsWith('player.') && 
      (path === 'player.firstName' || 
       path === 'player.lastName' || 
       path === 'player.name')) {
    return toSentenceCase(value);
  }
  
  return value;
};

/**
 * Processes text content to handle variable substitution, markdown-style links and line breaks
 * @param text - The input text to process
 * @param keyOffset - Offset for generating unique React keys
 * @param data - Primary data object for variable substitution
 * @param baseData - Secondary data object for variable substitution
 * @param player - Player object for player-specific variables
 * @param playerAssessment - Player assessment object for assessment-specific variables
 * @returns React.ReactNode with processed variables, links and line breaks
 */
export const processTextContent = (
  text: string, 
  keyOffset: number = 0,
  data?: any,
  baseData?: any,
  player?: IPlayer,
  playerAssessment?: IPlayerAssessment
): React.ReactNode => {
  if (!text || typeof text !== 'string') return text;

  // First substitute variables
  const textWithVariables = substituteVariables(text, data, baseData, player, playerAssessment);

  // Process markdown links first, then handle line breaks
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = linkRegex.exec(textWithVariables)) !== null) {
    // Add text before the link (including any line breaks)
    if (match.index > lastIndex) {
      const textBefore = textWithVariables.slice(lastIndex, match.index);
      parts.push(processLineBreaks(textBefore, parts.length + keyOffset));
    }
    
    // Add the link
    parts.push(
      <a 
        key={`link-${keyOffset}-${match.index}`} 
        href={match[2]} 
        target="_blank" 
        rel="noopener noreferrer"
        style={{ color: 'inherit', textDecoration: 'underline' }}
      >
        {match[1]}
      </a>
    );
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text after last link
  if (lastIndex < textWithVariables.length) {
    const textAfter = textWithVariables.slice(lastIndex);
    parts.push(processLineBreaks(textAfter, parts.length + keyOffset));
  }
  
  return parts.length > 1 ? parts : (parts[0] || textWithVariables);
};

/**
 * Helper function to convert \n to <br/> elements
 * @param text - Text to process for line breaks
 * @param keyOffset - Offset for generating unique React keys
 * @returns React.ReactNode with line breaks converted to <br/> elements
 */
export const processLineBreaks = (text: string, keyOffset: number = 0): React.ReactNode => {
  if (!text.includes('\n')) return text;
  
  const segments = text.split('\n');
  const result: React.ReactNode[] = [];
  
  segments.forEach((segment, index) => {
    if (index > 0) {
      result.push(<br key={`br-${keyOffset}-${index}`} />);
    }
    if (segment || index === 0) {
      result.push(segment);
    }
  });
  
  return result;
};

/**
 * Processes text for HTML rendering (used in components that use dangerouslySetInnerHTML)
 * @param text - The input text to process
 * @param data - Primary data object for variable substitution
 * @param baseData - Secondary data object for variable substitution
 * @param player - Player object for player-specific variables
 * @param playerAssessment - Player assessment object for assessment-specific variables
 * @returns HTML string with processed variables, links and line breaks
 */
export const processTextContentForHTML = (
  text: string,
  data?: any,
  baseData?: any,
  player?: IPlayer,
  playerAssessment?: IPlayerAssessment
): string => {
  if (!text || typeof text !== 'string') return text;

  // First substitute variables
  let processedText = substituteVariables(text, data, baseData, player, playerAssessment);

  // Process markdown links: [text](url) -> <a href="url" target="_blank" rel="noopener noreferrer">text</a>
  processedText = processedText.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g, 
    '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: inherit; text-decoration: underline;">$1</a>'
  );

  // Convert \n to <br/> elements
  processedText = processedText.replace(/\n/g, '<br/>');

  return processedText;
};